<template>
  <background-particles />
  <v-container fluid class="fill-height bg-primary">
    <v-row class="d-flex align-center justify-center">
      <v-col cols="12" md="4">
        <v-card v-motion-roll-left class="pa-4" elevation="24">
          <v-tabs
            v-model="mode"
            class="mb-3"
            grow
            bg-color="white"
            color="primary"
            density="compact"
          >
            <v-tab> Log In </v-tab>
            <v-tab> Register </v-tab>
          </v-tabs>
          <v-card-title class="text-primary text-center">
            <v-img
              src="@/assets/eye-academy-logo-light.svg"
              max-height="20"
              contain
            ></v-img>
            <Vue3Lottie
              class="my-6"
              :animationData="WelcomeJSON"
              :height="20"
              :width="250"
            />
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-img
                  id="google-login"
                  @click="loginGoogle"
                  v-ripple="{ class: 'text-red' }"
                  class="rounded"
                  src="@/assets/google_icon.svg"
                  max-height="45"
                  contain
                ></v-img>
              </v-col>
              <v-col cols="12">
                <div style="width: 100%" class="text-center">
                  <table width="100%">
                    <td>
                      <hr />
                    </td>
                    <td
                      style="width: 1px; padding: 0 10px; white-space: nowrap"
                    >
                      Or
                    </td>
                    <td>
                      <hr />
                    </td>
                  </table>
                </div>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="email"
                  density="compact"
                  clearable
                  autocomplete="email"
                  type="email"
                  label="Email"
                ></v-text-field>
                <v-text-field
                  v-model="password"
                  density="compact"
                  @click:append="togglePassword"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  clearable
                  autocomplete="password"
                  label="Password"
                  @keyup.enter="submit"
                ></v-text-field>
                <div v-if="!registerMode" class="text-caption text-left">
                  <forgot-password />
                </div>
                <v-text-field
                  v-if="registerMode"
                  v-model="cpassword"
                  density="compact"
                  @click:append="togglePassword"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  clearable
                  autocomplete="password"
                  label="Confirm Password"
                  @keyup.enter="submit"
                ></v-text-field>

                <div v-if="registerMode" class="text-caption px-4">
                  <div>Password Rules:</div>
                  <div v-for="(pc, idx) in passwordChecks" :key="idx">
                    <v-icon class="mr-2" :color="pc.valid ? 'green' : 'red'">{{
                      pc.valid ? "mdi-check-circle" : "mdi-close-circle"
                    }}</v-icon>
                    {{ pc.text }}
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-btn
                  :disabled="!enableButton"
                  @click="submit"
                  color="primary"
                  block
                  rounded="lg"
                  variant="flat"
                >
                  {{ modeText }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <div
            v-if="!registerMode"
            class="text-caption text-center font-weight-bold"
          >
            No account?
            <span style="cursor: pointer" class="text-primary" @click="mode = 1"
              >Register here.</span
            >
          </div>
          <div class="text-caption text-center">
            By continuing you agree to our
            <a class="text-primary" target="_blank" href="/terms">terms</a>
            and
            <a class="text-primary" target="_blank" href="/privacy"
              >privacy policy</a
            >.
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, reactive } from "vue";
import WelcomeJSON from "@/assets/welcome.json";
import BackgroundParticles from "@/components/BackgroundParticles.vue";
import ForgotPassword from "@/components/ForgotPassword.vue";
import { useAlertStore } from "@/store/alert";
const alertStore = useAlertStore();

// email & password logic
const email = ref("");
const password = ref("");
const cpassword = ref("");
const showPassword = ref(false);
const togglePassword = () => (showPassword.value = !showPassword.value);

// password validation logic
import { usePasswordCheck } from "@/composables/passwordCheck";
const { passwordChecks, loginFormValid, registerFormValid } = usePasswordCheck(
  email,
  password,
  cpassword,
);
const enableButton = computed(() =>
  registerMode.value ? registerFormValid.value : loginFormValid.value,
);

// form control logic
// define mode data
// 0 is login, 1 is register
const mode = ref(0);
const registerMode = computed(() => mode.value === 1);
const modeText = computed(() => (registerMode.value ? "Register" : "Log In"));

import {
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useFirebaseAuth } from "vuefire";
const auth = useFirebaseAuth();
const provider = new GoogleAuthProvider();
import { useRoute, useRouter } from "vue-router";
import { normalizeMcpLoginRedirect } from "@/utils/mcpDrafts.mjs";
const router = useRouter();
const route = useRoute();
const loginDestination = computed(() =>
  normalizeMcpLoginRedirect(route.query.redirect));
import { useUserStore } from "@/store/user";
const userStore = useUserStore();

const loginGoogle = () => {
  signInWithPopup(auth, provider)
    .then(async (result) => {
      await userStore.getBasicUserData();
      if (userStore.basic.onboarded) {
        router.push(loginDestination.value);
      } else {
        router.push("/onboarding");
      }
    })
    .catch((error) => {
      console.error("Error with Google login: ", error);
      alertStore.showAlert(
        "There was an error logging you in. Please try again or contact support.",
        "error",
      );
    });
};

const registerWithPassword = () => {
  createUserWithEmailAndPassword(auth, email.value, password.value)
    .then((userCredential) => {
      const user = userCredential.user;
      sendEmailVerification(user)
        .then(() => {
          // Email sent.
          userStore.getBasicUserData();
          router.push("/verify");
        })
        .catch((error) => {
          console.error("Error sending verification email: ", error);
          alertStore.showAlert(
            "There was an error sending the verification email. Please try again or contact support.",
            "error",
          );
        });
    })
    .catch((error) => {
      console.error("Error registering: ", error);
      let message;
      if (error.code === "auth/email-already-in-use") {
        message =
          "This account has already been registered. Either use the Google login or use the 'Forgot Password' button in the 'Log In' tab.";
      } else {
        message =
          "There was an error registering you. Please try again or contact support.";
      }
      alertStore.showAlert(message, "error");
    });
};

const loginWithPassword = () => {
  signInWithEmailAndPassword(auth, email.value, password.value)
    .then((userCredential) => {
      router.push(loginDestination.value);
    })
    .catch((error) => {
      console.error("Error with login: ", error);
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-login-credentials" ||
        error.code === "auth/invalid-credential"
      ) {
        alertStore.showAlert(
          "Incorrect email address or password. Please try again.",
          "error",
        );
      } else {
        alertStore.showAlert(
          "There was an error logging you in. Please try again or contact support.",
          "error",
        );
      }
      ``;
    });
};

const submit = () => {
  if (registerMode.value) {
    registerWithPassword();
  } else {
    loginWithPassword();
  }
};

// reset password logic
const resetEmail = ref("");
const resetPassword = () => {
  sendPasswordResetEmail(auth, resetEmail.value)
    .then(() => {
      alertStore.showAlert("An email .", "error");
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      // ..
    });
};
</script>

<style>
#google-login {
  border: rgb(var(--v-theme-error)) 1px solid;
  cursor: pointer;
}

#google-login:hover {
  background-color: rgba(var(--v-theme-error), 0.1);
}
</style>
